import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../ui/Button';
import { mcpService, type ManagedMcpIntegration } from '../../../services/mcpService';
import McpIntegrationTile from './McpIntegrationTile';
import { Surface } from '../../ui/Surface';

const IntegrationsTab: React.FC<{ agent: any }> = ({ agent }) => {
  const [integrations, setIntegrations] = useState<ManagedMcpIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadIntegrations = useCallback(async () => {
    if (!agent?.id) return;
    setIsLoading(true);
    try {
      const result = await mcpService.listManagedIntegrations(agent.id);
      setIntegrations(result.items || []);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load available integrations');
    } finally {
      setIsLoading(false);
    }
  }, [agent?.id]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const sortedIntegrations = useMemo(
    () => [...integrations].sort((left, right) => left.name.localeCompare(right.name)),
    [integrations]
  );

  return (
    <div className="space-y-6 p-6">
      <Surface as="section" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Available Integrations</h3>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              Select an integration and click Connect to configure credentials in a popup.
              Integrations are isolated per server for easier management.
            </p>
          </div>
          <Button variant="secondary" onClick={loadIntegrations} disabled={isLoading || !agent?.id}>
            Refresh
          </Button>
        </div>
      </Surface>

      {!sortedIntegrations.length ? (
        <section className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center shadow-sm">
          <h4 className="text-lg font-semibold text-slate-900">No integrations are enabled for this agent type</h4>
          <p className="mt-2 text-sm text-slate-500">
            A superadmin needs to publish integrations to the master MCP catalog and enable them on this agent type first.
          </p>
        </section>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {sortedIntegrations.map((integration) => (
          <McpIntegrationTile
            key={integration.id}
            integration={integration}
            agentId={agent.id}
            onChanged={loadIntegrations}
          />
        ))}
      </div>
    </div>
  );
};

export default IntegrationsTab;
